import React from "react";
import { Link as ReactRouterLink, LinkProps } from "react-router-dom";

import { onBrowser } from "@kazoohr/helpers";

interface KazooLinkProps extends LinkProps {
  to: string;
}

/**
 * A slim wrapper around our `Link` component that lets use use full path URLs
 * everywhere, but still take advantage of SPA capabilities by transforming
 * full paths to relative paths where the root path matches.
 *
 * In the future, this component will be vital for transforming us into a full
 * SPA.
 */
export function Link(props: KazooLinkProps) {
  try {
    const url = urlFromProps(props);
    // If we're on the server, we can't really know what URL we're at, so just use
    // the full path and a regular link.
    if (!onBrowser()) {
      return <a {...props} href={url} />;
    }

    // We can safely create a new URL() since urlFromProps normalizes the URL even if it's just a path.
    const urlObject = new URL(url);
    const isOnSameDomain = window.location.host === urlObject.host;

    if (isOnSameDomain && shouldSpa(url)) {
      return (
        <ReactRouterLink
          {...props}
          to={maybeAttachQueryParams(url, urlObject.pathname)}
        />
      );
    }

    // In this path, we're navigating outside of the SPA, so use an anchor
    return <a {...props} href={url} />;
  } catch (error) {
    // We _don't_ want to swallow these errors, but we don't want them to tank
    // the user experience if something goes wrong above. This allows us to find
    // these errors via our error trackers.
    /* eslint-disable-next-line no-console */
    console.error({ error });
    return <ReactRouterLink {...props} />;
  }
}

/**
 * Reduces cyclomatic complexity of our link component.
 */
export function urlFromProps(props: LinkProps): string {
  const url = props.to?.toString() || props.href || "";
  if (!onBrowser()) {
    return url;
  }
  if (url.substring(0, 6) === "mailto") {
    return url;
  }
  if (!url.includes("//")) {
    return `${window.location.origin}${url}`;
  }
  return url;
}

export function shouldSpa(url: string): boolean {
  return !isUrlExcludedFromSpa(url);
}

export function isUrlExcludedFromSpa(url: string): boolean {
  for (const excluded of excludedRoutesFromCombinedBundle) {
    if (url.endsWith(excluded)) {
      return true;
    }
  }
  return false;
}

// These are all things that we don't want part of the SPA flow.
// TODO: eliminate these by replacing them with KW native things
const excludedRoutesFromCombinedBundle = [
  "/anniversaries",
  "/awards",
  "/birthdays",
  "/kazoo/logout",
  "/my-grs-redemptions",
  "/my-redemptions",
  "/surveys",
  "/auth/logout",
];

/**
 * Takes a URL and, if valid, returns an application string. If it's not a valid
 * Kazoo URL, we instead return a Symbol (as a means of falsifying comparisons).
 */
export function urlToApplication(url: string): string | symbol {
  try {
    if (url.includes("//")) {
      // Prevents invalid URLs if we're using relative protocol. The URL class
      // will throw if we feed it a relative protocol.
      url = url.substr(0, 2) === "//" ? `http:${url}` : url;
      const urlObject = new URL(url);
      return urlObject.pathname.split("/")[1];
    }
    url = url[0] === "/" ? url : `/${url}`;
    return url.split("/")[1];
  } catch (e) {
    return Symbol("Invalid URL");
  }
}

export function maybeAttachQueryParams(
  originalUrl: string,
  newUrl: string
): string {
  if (!originalUrl.includes("?")) {
    return newUrl;
  }
  return newUrl + "?" + originalUrl.split("?")[1];
}
